/**
 * Stable node identity for CSS emission. The id lands in a CSS attribute
 * selector (cssEmitter.js), so the format is deliberately narrow and
 * re-validated on read: `wm` + 8 lowercase alphanumeric characters.
 *
 * Plain JS (not TS): this module is imported both by the Vite build
 * (editor-v2) and by the Vue-2 webpack build — the latter cannot resolve
 * .ts files. Types live in wmId.d.ts (precedent: cssEmitter.js).
 */

// The Vue-2 host's ESLint env config predates the `globalThis` global;
// the directive below declares it so the CI lint job (which gates this
// directory) passes without touching the shared .eslintrc. Keep the
// directive bare — this ESLint version parses any prose inside it as
// additional global names.
/* global globalThis */

export const WM_ID_ATTR = 'data-v2-id';
export const WM_COL_ATTR = 'data-v2-col';

/** The only valid shape of a wm id; the Bereich validator checks cell and entry ids with it too. */
export const WM_ID_RE = /^wm[a-z0-9]{8}$/;
const ID_RE = WM_ID_RE;
const ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

/**
 * Generates a fresh id matching ID_RE. Uses crypto.getRandomValues — no
 * Math.random fallback, per the (documented) assumption that it is always
 * available, both in the canvas (Vue 3, browser) and in jsdom-based tests.
 */
export function generateWmId() {
  let out = 'wm';
  const bytes = new Uint8Array(8);
  (globalThis.crypto || window.crypto).getRandomValues(bytes);
  for (let i = 0; i < 8; i += 1) out += ALPHABET[bytes[i] % ALPHABET.length];
  return out;
}

/**
 * Ensures `el` carries a valid WM_ID_ATTR, generating one if absent or
 * invalid (re-validated against ID_RE, not just "is present" — a value
 * written by something else, or corrupted in a way that breaks out of the
 * CSS attribute selector, must be replaced rather than trusted). Returns the
 * (possibly newly assigned) id.
 */
export function ensureWmId(el) {
  const existing = el.getAttribute(WM_ID_ATTR);
  if (existing !== null && ID_RE.test(existing)) return existing;
  const next = generateWmId();
  el.setAttribute(WM_ID_ATTR, next);
  return next;
}

/**
 * Regenerates WM_ID_ATTR on `root` and every descendant that carries it, so a
 * cloned/duplicated subtree never shares a CSS rule with its source (the
 * emitter selects by id). Review Critical 2: the sweep only touches nodes
 * whose CURRENT value already matches ID_RE — this is deliberate, not
 * redundant with ensureWmId's own re-validation. A node from an unrelated
 * feature (e.g. an AI-snippet node stamped with a server key like "head-a1b")
 * can also carry WM_ID_ATTR in a *different, non-wm-shaped* format; without
 * this pre-filter, removeAttribute+ensureWmId would treat that value as
 * "invalid" and silently replace the server key with a fresh wm-id, making
 * the node permanently unaddressable for its own feature. Nodes that don't
 * match ID_RE are left completely untouched (attribute, value, everything).
 *
 * Optional `presetIds`: assigns these ids positionally (in the same order
 * `root`+its id-bearing descendants are visited) instead of generating fresh
 * random ones — used by regenerateWmIdsInHtml (Review Minor M-c) so two
 * string representations of the same element (`html`/`originalHtml`) end up
 * with the SAME regenerated ids rather than two independent random sets.
 * Returns the final ids, in visiting order, for the caller to reuse.
 */
export function regenerateWmIds(root, presetIds) {
  const idBearers = [root, ...Array.from(root.querySelectorAll(`[${WM_ID_ATTR}]`))]
    .filter((el) => {
      const value = el.getAttribute(WM_ID_ATTR);
      return value !== null && ID_RE.test(value);
    });
  return idBearers.map((el, i) => {
    el.removeAttribute(WM_ID_ATTR);
    const preset = presetIds ? presetIds[i] : undefined;
    if (preset) {
      el.setAttribute(WM_ID_ATTR, preset);
      return preset;
    }
    return ensureWmId(el);
  });
}

/**
 * String-oriented sibling of regenerateWmIds, for call sites that hold a
 * snippet as an HTML string rather than a live DOM node (e.g. store-level
 * duplicate/paste, which clones a JSON-serialised Element whose `.html` /
 * `.originalHtml` fields are plain strings). Parses into a detached
 * container, regenerates ids on each top-level element, and serialises back.
 * The cheap `indexOf` bail-out avoids parsing the (much more common) v1
 * snippet HTML that never carries WM_ID_ATTR at all.
 *
 * Returns `{ html, ids }` — `ids` is one array of assigned ids per top-level
 * element (matching `presetIds`' shape), so a caller holding two string
 * representations of the same element (`html` + `originalHtml`, Review Minor
 * M-c) can regenerate the first, then pass its `ids` back in as `presetIds`
 * for the second — both end up carrying the identical id set instead of two
 * independently randomised ones.
 */
export function regenerateWmIdsInHtml(html, presetIds) {
  if (typeof html !== 'string' || html.indexOf(WM_ID_ATTR) === -1) {
    return { html, ids: [] };
  }
  const container = document.createElement('div');
  container.innerHTML = html;
  const ids = Array.from(container.children).map(
    (child, i) => regenerateWmIds(child, presetIds ? presetIds[i] : undefined),
  );
  return { html: container.innerHTML, ids };
}
